/**
 * Audio Capture Manager
 * Sets up and manages audio capture using AudioWorkletProcessor
 * Handles microphone access, audio processing, and level monitoring
 */

import { getMicrophoneConstraints, int16ArrayToBase64 } from './audioUtils';
import { checkMicrophonePermission } from './microphonePermission';
import logger from './logger';

export interface AudioCaptureConfig {
  onAudioData?: (base64Audio: string) => void;
  onAudioLevel?: (level: number) => void;
  onError?: (error: Error) => void;
  onNoAudioDetected?: () => void;
  deviceId?: string; // Specific microphone device ID to use
  onFrequencyData?: (frequencyData: Uint8Array) => void; // Frequency spectrum for visualization
}

export class AudioCaptureManager {
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private config: AudioCaptureConfig = {};
  private noAudioTimer: NodeJS.Timeout | null = null;
  private hasDetectedAudio: boolean = false;
  private readonly NO_AUDIO_TIMEOUT_MS = 10000; // 10 seconds
  private analyser: AnalyserNode | null = null;
  private frequencyUpdateInterval: NodeJS.Timeout | null = null;
  private isStopped: boolean = false; // Guard against stop() being called during async start()

  /**
   * Initialize and start audio capture
   */
  async start(config: AudioCaptureConfig = {}): Promise<void> {
    this.config = config;
    this.isStopped = false; // Reset for new start

    try {
      logger.log('[AudioCaptureManager] Starting audio capture');
      
      // Check microphone permission first
      const permissionState = await checkMicrophonePermission();
      logger.log('[AudioCaptureManager] Permission state:', permissionState);
      
      if (this.isStopped) {
        logger.log('[AudioCaptureManager] stop() called during permission check - aborting start');
        return;
      }
      
      if (permissionState === 'denied') {
        throw new Error('Microphone permission denied. Please enable it in your browser settings.');
      }
      
      // Get microphone access (will prompt if needed)
      logger.log('[AudioCaptureManager] Requesting microphone access...', config.deviceId ? `(device: ${config.deviceId})` : '(default device)');
      const stream = await navigator.mediaDevices.getUserMedia(getMicrophoneConstraints(config.deviceId));

      // If stop() was called while getUserMedia was pending, release the stream immediately
      if (this.isStopped) {
        logger.log('[AudioCaptureManager] stop() called during getUserMedia - releasing stream immediately');
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      this.mediaStream = stream;
      logger.log('[AudioCaptureManager] Microphone access granted');

      // Create audio context with 24kHz sample rate
      this.audioContext = new AudioContext({ sampleRate: 24000 });
      logger.log('[AudioCaptureManager] AudioContext created:', {
        sampleRate: this.audioContext.sampleRate,
        state: this.audioContext.state
      });

      // Load the audio worklet processor
      const workletPath = `${window.location.origin}/audio-processor-worklet.js`;
      logger.log('[AudioCaptureManager] Loading worklet from:', workletPath);
      await this.audioContext.audioWorklet.addModule(workletPath);

      // Create the worklet node
      this.workletNode = new AudioWorkletNode(this.audioContext, 'audio-capture-processor');

      // Listen for audio data from the worklet
      this.workletNode.port.onmessage = (event) => {
        const { audioData, audioLevel } = event.data;
        
        if (!this.workletNode) return;
        
        // Log every chunk so we can see levels in console
        logger.log('[AudioCaptureManager] chunk level:', audioLevel.toFixed(4), '| hasDetectedAudio:', this.hasDetectedAudio, '| timer active:', !!this.noAudioTimer);

        // Check if we've detected audio (level > threshold)
        // 0.05 avoids false positives from mic noise floor on OS-muted devices
        if (audioLevel > 0.05 && !this.hasDetectedAudio) {
          this.hasDetectedAudio = true;
          logger.log('[AudioCaptureManager] ✅ Audio DETECTED above threshold. Level:', audioLevel.toFixed(4), '- clearing no-audio timer');
          // Clear the no-audio timer since we detected audio
          if (this.noAudioTimer) {
            clearTimeout(this.noAudioTimer);
            this.noAudioTimer = null;
          }
        }
        
        // Convert to base64 for transmission
        const int16Array = new Int16Array(audioData);
        const base64Audio = int16ArrayToBase64(int16Array);
        
        // Emit audio data
        this.config.onAudioData?.(base64Audio);
        
        // Emit audio level for visualization
        this.config.onAudioLevel?.(audioLevel);
      };

      // Connect microphone to worklet
      const source = this.audioContext.createMediaStreamSource(this.mediaStream);
      source.connect(this.workletNode);
      
      // Set up frequency analyzer for visualization if callback provided
      if (this.config.onFrequencyData) {
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 2048; // Higher FFT for better frequency resolution (1024 bins)
        this.analyser.smoothingTimeConstant = 0.6; // Moderate smoothing for responsive voice
        this.analyser.minDecibels = -85; // Adjusted for voice levels
        this.analyser.maxDecibels = -25; // Adjusted for voice levels
        
        source.connect(this.analyser);
        
        const frequencyData = new Uint8Array(this.analyser.frequencyBinCount);
        
        // Update frequency data at 60 FPS
        this.frequencyUpdateInterval = setInterval(() => {
          if (this.analyser) {
            this.analyser.getByteFrequencyData(frequencyData);
            this.config.onFrequencyData?.(frequencyData);
          }
        }, 1000 / 60);
        
        logger.log('[AudioCaptureManager] Frequency analyzer initialized with', this.analyser.frequencyBinCount, 'bins for voice spectrum');
        logger.log('[AudioCaptureManager] Voice frequency mapping: 100Hz-4000Hz across visualization bars');
      }
      
      logger.log('[AudioCaptureManager] Audio capture pipeline connected');
      logger.log('[AudioCaptureManager] Now capturing audio...');
      
      // Start timer to detect if no audio is received within timeout
      logger.log('[AudioCaptureManager] ⏱️ Starting no-audio timer for', this.NO_AUDIO_TIMEOUT_MS / 1000, 'seconds');
      this.noAudioTimer = setTimeout(() => {
        logger.log('[AudioCaptureManager] ⏰ No-audio timer FIRED. hasDetectedAudio:', this.hasDetectedAudio);
        if (!this.hasDetectedAudio) {
          logger.log('[AudioCaptureManager] 🔇 No audio detected - calling onNoAudioDetected callback');
          console.warn('[AudioCaptureManager] No audio detected after', this.NO_AUDIO_TIMEOUT_MS / 1000, 'seconds');
          this.config.onNoAudioDetected?.();
        } else {
          logger.log('[AudioCaptureManager] ✅ Timer fired but audio was already detected - suppressing callback');
        }
      }, this.NO_AUDIO_TIMEOUT_MS);

    } catch (error) {
      console.error('[AudioCaptureManager] Failed to start audio capture:', error);
      this.config.onError?.(error as Error);
      await this.stop();
      throw error;
    }
  }

  /**
   * Stop audio capture and cleanup resources
   */
  async stop(): Promise<void> {
    logger.log('[AudioCaptureManager] Stopping audio capture');
    this.isStopped = true; // Signal any in-progress start() to abort
    
    // Clear the no-audio timer
    if (this.noAudioTimer) {
      clearTimeout(this.noAudioTimer);
      this.noAudioTimer = null;
    }
    
    // Clear frequency update interval
    if (this.frequencyUpdateInterval) {
      clearInterval(this.frequencyUpdateInterval);
      this.frequencyUpdateInterval = null;
    }
    
    // Reset detection flag
    this.hasDetectedAudio = false;

    // Stop media stream tracks FIRST to remove browser recording indicator
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => {
        track.stop();
        logger.log('[AudioCaptureManager] Stopped track:', track.label);
      });
      this.mediaStream = null;
    }

    // Disconnect and cleanup worklet node (this stops the audio processing)
    if (this.workletNode) {
      // Remove the message handler to stop receiving audio chunks
      this.workletNode.port.onmessage = null;
      this.workletNode.disconnect();
      this.workletNode = null;
    }
    
    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }

    // Close audio context last
    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
    }

    logger.log('[AudioCaptureManager] Audio capture stopped and cleaned up');
  }

  /**
   * Check if currently capturing
   */
  isCapturing(): boolean {
    return this.audioContext?.state === 'running' && this.mediaStream !== null;
  }

  /**
   * Get the current audio context state
   */
  getState(): AudioContextState | 'inactive' {
    return this.audioContext?.state ?? 'inactive';
  }
}

// Export singleton instance
export const audioCaptureManager = new AudioCaptureManager();
